import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Reveal } from "./Reveal";
import HeaderBg, { STUDIO } from "./HeaderBg";

const SPAN_BASIS = {
  "col-span-12 md:col-span-8": "md:basis-[calc(66.666%-0.75rem)]",
  "col-span-12 md:col-span-6": "md:basis-[calc(50%-0.75rem)]",
  "col-span-12 md:col-span-4": "md:basis-[calc(33.333%-0.75rem)]",
};

export default function Gallery({ items = [] }) {
  const [index, setIndex] = useState(null);
  const [visible, setVisible] = useState(12);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(() => setIndex((i) => (i === null ? i : (i - 1 + items.length) % items.length)), [items.length]);
  const next = useCallback(() => setIndex((i) => (i === null ? i : (i + 1) % items.length)), [items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, prev, next]);

  const current = open ? items[index] : null;

  return (
    <section id="gallery" className="relative bg-obsidian py-24 md:py-40" data-testid="gallery-section">
      <HeaderBg image={STUDIO.desk} />
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal className="mb-16 md:mb-24 max-w-2xl">
          <p className="overline text-gold mb-6">The Visual Vault</p>
          <h2 className="headline text-5xl md:text-7xl text-white">Iconography</h2>
        </Reveal>

        <div className="flex flex-wrap justify-center gap-2 md:gap-3">
          {items.slice(0, visible).map((g, i) => (
            <motion.figure
              key={g.id}
              onClick={() => setIndex(i)}
              className={`relative overflow-hidden group cursor-pointer grow-0 shrink-0 basis-full ${SPAN_BASIS[g.span] || "md:basis-[calc(33.333%-0.75rem)]"}`}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`gallery-item-${i}`}
            >
              {g.kind === "video" ? (
                <div className="relative w-full h-[260px] md:h-[420px] bg-black overflow-hidden">
                  {g.poster && (
                    <img
                      src={g.poster}
                      alt={g.caption}
                      loading="lazy"
                      style={{ objectPosition: "center 25%" }}
                      className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms] group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/45 transition-colors" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gold/90 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                      <Play size={28} className="text-black ml-1" />
                    </span>
                  </span>
                  <span className="absolute top-4 right-4 font-display uppercase tracking-[0.2em] text-[0.58rem] text-white/80 border border-white/25 rounded-full px-3 py-1 bg-black/40">
                    Clip
                  </span>
                </div>
              ) : (
                <img
                  src={g.thumb || g.image || undefined}
                  alt={g.caption}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: "center 22%" }}
                  className="w-full h-[260px] md:h-[420px] object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms] group-hover:scale-105"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </motion.figure>
          ))}
        </div>

        {visible < items.length && (
          <div className="mt-12 md:mt-16 flex justify-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + 12)}
              data-testid="gallery-load-more"
              className="group inline-flex items-center gap-3 font-display uppercase tracking-[0.2em] text-sm text-white border border-white/25 px-9 py-4 hover:bg-gold hover:text-black hover:border-gold transition-colors duration-300"
            >
              Load More
              <span className="text-white/50 group-hover:text-black transition-colors">
                {visible} / {items.length}
              </span>
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {open && current && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[95] bg-black/95 backdrop-blur-md flex items-center justify-center"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button onClick={close} data-testid="lightbox-close" aria-label="Close"
              className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-gold hover:text-black hover:border-gold transition-colors">
              <X size={22} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" aria-label="Previous"
              className="absolute left-3 md:left-8 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-gold hover:text-black hover:border-gold transition-colors">
              <ChevronLeft size={26} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" aria-label="Next"
              className="absolute right-3 md:right-8 z-10 w-12 h-12 md:w-14 md:h-14 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-gold hover:text-black hover:border-gold transition-colors">
              <ChevronRight size={26} />
            </button>

            <motion.figure
              key={current.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.4 }}
              className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {current.kind === "video" ? (
                <video
                  src={current.image}
                  poster={current.poster || undefined}
                  controls
                  autoPlay
                  playsInline
                  className="max-w-[92vw] max-h-[82vh] rounded"
                  data-testid="lightbox-video"
                />
              ) : (
                <img
                  src={current.image}
                  alt={current.caption}
                  className="max-w-[92vw] max-h-[80vh] object-contain"
                  data-testid="lightbox-image"
                />
              )}
              <figcaption className="mt-4 font-display uppercase tracking-[0.2em] text-sm text-white/60">
                {index + 1} / {items.length}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
